import { type FC, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import AddGreyIcon from '@assets/img/icons/add-icon.svg?react';
import AddGreenIcon from '@assets/img/icons/add-icon-active.svg?react';
import InfoIcon from '@assets/img/icons/info-purple.svg?react';
import cn from 'classnames';

import { useCheckVIPstatus } from '@/common/hooks/checkVIPstatus';
import { timeStringFormat } from '@/common/lib/timer';
import { ModalContext } from '@/components/Modals/Modal/config';
import { H2, StringSmall } from '@/components/Typography/Typography';
import { Button } from '@/components/ui/Button/Button';
import { MotionPicture } from '@/components/ui/Picture/Picture';
import { useAppSelector } from '@/redux/store';

import { DiceGameContext } from '../context';
import { DiceGameModalsContext } from '../DiceGameModals/context';
import { RecoverDiceEnergyContext } from '../RecoverDiceEnergyProvider/context';

import css from './styles.module.scss';

import dicePng from '/img/dice-game/dice.png';
import diceWebp from '/img/dice-game/dice.webp';
import flashPng from '/img/icons/flash.png';
import flashWebp from '/img/icons/flash.webp';

interface MainGameBalanceProps {
  type: 'energy' | 'dice';
  className?: string;
}

const MainGameBalance: FC<MainGameBalanceProps> = ({ type, className }) => {
  const vip = useCheckVIPstatus();
  const { t } = useTranslation();
  const { setEnergyToRef, flyingAnimation, setDiceToRef } =
    useContext(DiceGameContext);
  const { openRechargeEnergy } = useContext(DiceGameModalsContext);
  const { openModal } = useContext(ModalContext);
  const { currentTimeToRecoverOneEnergy } = useContext(
    RecoverDiceEnergyContext,
  );
  const targetEnergyRef = useRef<HTMLImageElement | null>(null);
  const targetDiceRef = useRef<HTMLImageElement | null>(null);
  const energy = useAppSelector((state) => state.diceGame.user?.energy);
  const diceCount = useAppSelector((state) => state.diceGame.user?.diceCount);
  const energyLimit = vip ? 15 : 10;

  const conditionForPlusIcon =
    type === 'energy' ? (energy || 0) < energyLimit : true;
  const disabled = type === 'energy' ? energy === energyLimit : false;

  const energyFlyAnimationActive =
    type === 'energy' && flyingAnimation === 'energy';
  const diceFlyAnimationActive = type === 'dice' && flyingAnimation === 'dice';

  useEffect(() => {
    if (targetEnergyRef.current) {
      setEnergyToRef(targetEnergyRef);
    }

    if (targetDiceRef.current) {
      setDiceToRef(targetDiceRef);
    }
  }, []);

  const image =
    type === 'energy'
      ? { img: flashPng, webp: flashWebp }
      : { img: dicePng, webp: diceWebp };

  return (
    <div className={cn(css.balance, className)}>
      <div className="relative">
        <MotionPicture
          ref={type === 'energy' ? targetEnergyRef : targetDiceRef}
          {...image}
          className="w-7 h-7 block shrink-0"
          alt={type}
          animate={{
            scale: energyFlyAnimationActive || diceFlyAnimationActive ? 1.4 : 1,
          }}
        />
        <InfoIcon className="w-3 h-3 absolute right-0 bottom-0 text-white z-[1]" />
        <span className="w-[0.625rem] h-[0.625rem] rounded-full bg-white absolute right-[0.125rem] bottom-[0.125rem]"></span>
      </div>
      <H2 weight="bold">
        {type === 'energy' ? (
          <>
            <span>{energy}</span>
            <span className="text-white/40">/{energyLimit}</span>
          </>
        ) : (
          diceCount
        )}
      </H2>
      <Button
        onClick={
          type === 'energy'
            ? openRechargeEnergy
            : (): void =>
                openModal({ renderKind: 'buyDiceModal', props: undefined })
        }
        variant="transparent"
        className="!w-auto"
        disabled={disabled}
      >
        {conditionForPlusIcon ? (
          <AddGreenIcon className="w-8 h-8 shrink-0" />
        ) : (
          <AddGreyIcon className="w-8 h-8 shrink-0 text-white/40" />
        )}
      </Button>
      {type === 'energy' && (
        <div className={css.timer}>
          <StringSmall className="!text-white/80">{t('Recharge')}:</StringSmall>
          <div className="flex items-center justify-end w-[4.5rem]">
            <StringSmall className="!text-white/80 italic">
              {timeStringFormat(currentTimeToRecoverOneEnergy * 1000, {
                template: '{minutes}m : {seconds}s',
              })}
            </StringSmall>
          </div>
        </div>
      )}
    </div>
  );
};

export { MainGameBalance };
